import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  consumerSearch,
  getPublicSearchHot,
  type ConsumerSearchResultItem,
  type PublicHotKeywordItem,
} from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { useTk } from '@/lib/i18n';
import { trackBehavior } from '@/lib/track';
import { useAuth } from '@/lib/use-auth';
import { useSelectedCity } from '@/lib/use-city';
import { Button } from '@sabay/ui-kit';

interface LoadState {
  status: 'idle' | 'loading' | 'success' | 'error';
  results: ConsumerSearchResultItem[];
  total: number;
  errorMessage: string | null;
}

const DEBOUNCE_MS = 250;

function resultHref(item: ConsumerSearchResultItem): string {
  switch (item.type) {
    case 'merchant':
      return `/merchants/${item.id}`;
    case 'groupon':
      return `/groupons/${item.id}`;
    case 'discover':
      return `/discover/${item.id}`;
    default:
      return '/discover';
  }
}

export function SearchPage(): React.ReactElement {
  const tk = useTk();
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cityCode] = useSelectedCity();
  const initialQ = searchParams.get('q') ?? '';

  const [input, setInput] = useState(initialQ);
  const [state, setState] = useState<LoadState>({
    status: initialQ ? 'loading' : 'idle',
    results: [],
    total: 0,
    errorMessage: null,
  });
  const [hot, setHot] = useState<PublicHotKeywordItem[]>([]);

  const acRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (input.trim()) return;
    const ac = new AbortController();
    getPublicSearchHot(cityCode, ac.signal)
      .then((r) => setHot(r.items))
      .catch(() => setHot([]));
    return () => ac.abort();
  }, [cityCode, input]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (acRef.current) acRef.current.abort();

    const trimmed = input.trim();
    if (!trimmed) {
      setState({ status: 'idle', results: [], total: 0, errorMessage: null });
      if (searchParams.get('q')) {
        const next = new URLSearchParams(searchParams);
        next.delete('q');
        setSearchParams(next, { replace: true });
      }
      return;
    }

    timerRef.current = setTimeout(() => {
      const ac = new AbortController();
      acRef.current = ac;
      setState((s) => ({ ...s, status: 'loading', errorMessage: null }));

      consumerSearch(trimmed, { cityCode, type: 'all', page: 1, pageSize: 30 }, ac.signal)
        .then((res) => {
          setState({
            status: 'success',
            results: res.results,
            total: res.total,
            errorMessage: null,
          });
          const next = new URLSearchParams(searchParams);
          next.set('q', trimmed);
          setSearchParams(next, { replace: true });
          const t = authRef.current.token;
          if (authRef.current.status === 'authenticated' && t) {
            void trackBehavior('SEARCH_QUERY', { q: trimmed, cityCode, total: res.total }, t);
          }
        })
        .catch((err: Error) => {
          if (ac.signal.aborted) return;
          setState({
            status: 'error',
            results: [],
            total: 0,
            errorMessage: err.message,
          });
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [input, cityCode, searchParams, setSearchParams]);

  return (
    <main className="min-h-screen space-y-4 px-6 py-6">
      <header className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="p-0 text-sm text-[var(--tg-link)]"
          aria-label="back"
        >
          ←
        </Button>
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={tk('home.search.placeholder')}
          className="flex-1 rounded-lg bg-neutral-100 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          autoFocus
          enterKeyHint="search"
        />
      </header>

      {state.status === 'idle' ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500">
            {tk('search.hint.empty_input')}
          </div>
          {hot.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
                {tk('search.hot.label')}
              </p>
              <div className="flex flex-wrap gap-2">
                {hot.map((h) => (
                  <button
                    key={`${h.queryText}-${h.cityCode ?? 'all'}`}
                    type="button"
                    className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs text-neutral-800"
                    onClick={() => setInput(h.queryText)}
                  >
                    {h.queryText}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="py-6 text-center text-sm text-neutral-400">{tk('common.loading')}</div>
      ) : null}

      {state.status === 'error' ? (
        <div className="rounded-lg border border-danger-500 bg-danger-50 p-4 text-sm text-danger-700">
          <p className="font-medium">{tk('common.error.generic')}</p>
          <p className="mt-1 break-all text-xs opacity-75">{state.errorMessage}</p>
        </div>
      ) : null}

      {state.status === 'success' ? (
        <>
          <p className="text-xs text-[var(--tg-hint)]">
            {tk('search.result.count')}: {state.total}
          </p>
          {state.results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
              {tk('search.result.empty')}
            </div>
          ) : (
            <ul className="space-y-3">
              {state.results.map((item) => (
                <li key={`${item.type}-${item.id}`}>
                  <Link
                    to={resultHref(item)}
                    className="block rounded-lg border border-neutral-200 bg-white p-4 hover:border-brand-500"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-wide text-neutral-400">{item.type}</p>
                        <h3 className="truncate font-medium">{item.summary.title}</h3>
                        {item.summary.snippet ? (
                          <p className="mt-1 line-clamp-2 text-xs text-[var(--tg-hint)]">{item.summary.snippet}</p>
                        ) : null}
                      </div>
                      {item.summary.priceCents ? (
                        <div className="flex shrink-0 flex-col items-end">
                          <span className="text-[10px] text-neutral-400">{tk('search.result.from')}</span>
                          <span className="font-semibold text-brand-500">
                            {formatPrice(item.summary.priceCents, 'KHR')}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </main>
  );
}
